import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getMemberFlats,
    getMemberInvoices,
} from '../../../redux/actions/SocietyPortal/MemberPortalAction/MemberPortalAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import Pagination from '../../../components/common/Pagination';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_MY_BILL_DETAIL } from '../../../Routing/routes';
import {
    INVOICE_STATUS_STYLE,
    billStatusLabel,
    fmtCurrency,
    fmtDate,
    fmtPeriod,
    flatLabel,
    humanize,
    isOverdue,
} from './memberConstants';

const ITEMS_PER_PAGE = 10;

// Every maintenance invoice raised on the flats this member owns or occupies.
// Read-only: no post/void/delete, no bulk actions — those live on the admin
// Invoices screen behind INVOICE_* permissions.
const MyBills = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        flatsData,
        invoicesData,
        invoicesLoading,
        invoicesPagination,
    } = useSelector((state) => state.MemberPortalReducer || {});

    const [status, setStatus] = useState('');
    const [flatId, setFlatId] = useState('');
    const [page, setPage] = useState(1);

    const flats = useMemo(() => (Array.isArray(flatsData) ? flatsData : []), [flatsData]);

    useEffect(() => {
        if (societyId) dispatch(getMemberFlats({ societyId }));
    }, [dispatch, societyId]);

    const fetchBills = useCallback(() => {
        if (!societyId) return;
        dispatch(getMemberInvoices({
            societyId,
            page,
            limit: ITEMS_PER_PAGE,
            status: status || undefined,
            flatId: flatId || undefined,
        }));
    }, [dispatch, societyId, page, status, flatId]);
    usePageRefresh(fetchBills);

    useEffect(() => { fetchBills(); }, [fetchBills]);

    const rows = useMemo(() => (Array.isArray(invoicesData) ? invoicesData : []), [invoicesData]);
    const totalPages = invoicesPagination?.totalPages || 1;
    const totalItems = invoicesPagination?.total ?? rows.length;

    // Page-level total, labelled as such so it is never mistaken for the
    // member's full outstanding balance (that figure lives on My Home).
    const pageOutstanding = rows.reduce((sum, b) => sum + parseFloat(b.balance_due || 0), 0);

    const openBill = (id) =>
        navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_BILL_DETAIL.replace(':id', id));

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">

                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="hugeicons:invoice-02" className="tw-text-sky-600" />
                            My Bills
                        </h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            Maintenance invoices raised on {flats.length > 1 ? 'your flats' : 'your flat'}
                        </p>
                    </div>
                    {pageOutstanding > 0 && (
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-full tw-text-[11px] tw-font-semibold tw-bg-rose-50 tw-text-rose-700 tw-border tw-border-rose-100">
                            <Icon icon="mdi:alert-circle-outline" className="tw-text-sm" />
                            {fmtCurrency(pageOutstanding)} due on this page
                        </span>
                    )}
                </div>

                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-bg-gray-50/50 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                    <select
                        value={status}
                        onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                    >
                        <option value="">All bills</option>
                        <option value="POSTED">Unpaid</option>
                        <option value="PARTIAL">Partly paid</option>
                        <option value="PAID">Paid</option>
                    </select>
                    {/* Only worth showing when the member actually holds more than one flat. */}
                    {flats.length > 1 && (
                        <select
                            value={flatId}
                            onChange={(e) => { setFlatId(e.target.value); setPage(1); }}
                            className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                        >
                            <option value="">All my flats</option>
                            {flats.map((f) => (
                                <option key={f.flat_id} value={f.flat_id}>{flatLabel(f)}</option>
                            ))}
                        </select>
                    )}
                </div>

                <div className="tw-p-4">
                    {invoicesLoading && (
                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-12">
                            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-sky-500" />
                            <span className="tw-text-xs tw-text-gray-400">Loading...</span>
                        </div>
                    )}

                    {!invoicesLoading && rows.length === 0 && (
                        <div className="tw-text-center tw-py-14">
                            <Icon icon="hugeicons:invoice-02" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-2">
                                {status ? 'No bills match this filter' : 'No bills have been raised on your flat yet'}
                            </p>
                        </div>
                    )}

                    {!invoicesLoading && rows.length > 0 && (
                        <>
                            {/* Table on wide screens */}
                            <div className="tw-hidden md:tw-block tw-overflow-x-auto">
                                <table className="tw-w-full tw-text-xs">
                                    <thead>
                                        <tr className="tw-text-left tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500">
                                            <th className="tw-px-3 tw-py-2 tw-font-semibold">Bill no.</th>
                                            <th className="tw-px-3 tw-py-2 tw-font-semibold">Period</th>
                                            <th className="tw-px-3 tw-py-2 tw-font-semibold">Due date</th>
                                            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-text-right">Amount</th>
                                            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-text-right">Balance</th>
                                            <th className="tw-px-3 tw-py-2 tw-font-semibold">Status</th>
                                            <th className="tw-px-3 tw-py-2" />
                                        </tr>
                                    </thead>
                                    <tbody className="tw-divide-y tw-divide-gray-50">
                                        {rows.map((b) => {
                                            const label = billStatusLabel(b);
                                            const overdue = isOverdue(b);
                                            return (
                                                <tr
                                                    key={b.invoice_id}
                                                    onClick={() => openBill(b.invoice_id)}
                                                    className="hover:tw-bg-slate-50/60 tw-cursor-pointer"
                                                >
                                                    <td className="tw-px-3 tw-py-3 tw-font-mono tw-font-medium tw-text-gray-800">
                                                        <span className={overdue ? 'tw-border-l-2 tw-border-rose-500 tw-pl-2' : ''}>
                                                            {b.document_number || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="tw-px-3 tw-py-3 tw-text-gray-600">
                                                        {fmtPeriod(b.period_start, b.period_end)}
                                                    </td>
                                                    <td className="tw-px-3 tw-py-3 tw-text-gray-600">{fmtDate(b.due_date)}</td>
                                                    <td className="tw-px-3 tw-py-3 tw-text-right tw-text-gray-800">
                                                        {fmtCurrency(b.net_payable)}
                                                    </td>
                                                    <td className={`tw-px-3 tw-py-3 tw-text-right tw-font-semibold ${parseFloat(b.balance_due || 0) > 0 ? 'tw-text-rose-600' : 'tw-text-emerald-600'}`}>
                                                        {fmtCurrency(b.balance_due)}
                                                    </td>
                                                    <td className="tw-px-3 tw-py-3">
                                                        <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${INVOICE_STATUS_STYLE[label] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                            {humanize(label)}
                                                        </span>
                                                    </td>
                                                    <td className="tw-px-3 tw-py-3 tw-text-right">
                                                        <Icon icon="mdi:chevron-right" className="tw-text-gray-400 tw-text-base" />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Cards on small screens */}
                            <div className="md:tw-hidden tw-space-y-2">
                                {rows.map((b) => {
                                    const label = billStatusLabel(b);
                                    return (
                                        <button
                                            key={b.invoice_id}
                                            onClick={() => openBill(b.invoice_id)}
                                            className={`tw-w-full tw-text-left tw-border tw-rounded-lg tw-p-3 ${isOverdue(b) ? 'tw-border-rose-200 tw-bg-rose-50/30' : 'tw-border-gray-100'}`}
                                        >
                                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                                                <span className="tw-text-[11px] tw-font-mono tw-font-semibold tw-text-gray-700">
                                                    {b.document_number || '-'}
                                                </span>
                                                <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${INVOICE_STATUS_STYLE[label] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                    {humanize(label)}
                                                </span>
                                            </div>
                                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-1">
                                                {fmtPeriod(b.period_start, b.period_end)} &middot; due {fmtDate(b.due_date)}
                                            </p>
                                            <div className="tw-flex tw-items-center tw-justify-between tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-100">
                                                <span className="tw-text-[11px] tw-text-gray-500">
                                                    Bill {fmtCurrency(b.net_payable)}
                                                </span>
                                                <span className={`tw-text-sm tw-font-bold ${parseFloat(b.balance_due || 0) > 0 ? 'tw-text-rose-600' : 'tw-text-emerald-600'}`}>
                                                    {fmtCurrency(b.balance_due)}
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {totalPages > 1 && (
                                <div className="tw-pt-4 tw-mt-2 tw-border-t tw-border-gray-100">
                                    <Pagination
                                        currentPage={page}
                                        totalPages={totalPages}
                                        totalItems={totalItems}
                                        itemsPerPage={ITEMS_PER_PAGE}
                                        onPageChange={setPage}
                                        showItemsPerPage={false}
                                        itemLabel="bills"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyBills;
